import { Controller } from "stimulus"

export default class extends Controller {
  static targets = ["sendButton", "cancelButton", "backButton", "chatTextarea"]

  // Timeout in ms to auto-recover if streaming gets stuck
  static STREAMING_TIMEOUT = 60000

  connect() {
    console.log("Stop button controller connected")

    this.sendButton = this.hasSendButtonTarget ?
      this.sendButtonTarget :
      document.getElementById("send-button")

    this.cancelButton = this.hasCancelButtonTarget ?
      this.cancelButtonTarget :
      document.getElementById("cancel-button")

    this.backButton = this.hasBackButtonTarget ?
      this.backButtonTarget :
      document.getElementById("back-button")

    // Get reference to chat textarea
    this.chatTextarea = this.hasChatTextareaTarget ?
      this.chatTextareaTarget :
      document.getElementById("chatPromptField")

    if (!this.sendButton || !this.cancelButton || !this.backButton) {
      console.error("Stop button controller: Could not find all required buttons")
      return
    }

    if (!this.chatTextarea) {
      console.error("Stop button controller: Could not find chat textarea")
      return
    }

    this.streamingStartTime = null
    this.streamingTimeoutId = null

    this.updateButtonVisibility()

    setTimeout(() => {
      this.observeMessageList()
    }, 100)
  }

  disconnect() {
    console.log("Stop button controller disconnecting")
    if (this.observer) {
      this.observer.disconnect()
    }
    this.clearStreamingTimeout()
    // Re-enable textarea on disconnect to prevent it staying disabled
    this.enableChatTextarea()
  }

  observeMessageList() {
    if (this.observer) {
      this.observer.disconnect()
    }

    this.observer = new MutationObserver(() => {
      clearTimeout(this.updateTimeout)
      this.updateTimeout = setTimeout(() => {
        this.updateButtonVisibility()
      }, 50)
    })

    // Only observe the message list container, not the entire document body
    const messageList = document.getElementById("list-messages")
    if (messageList) {
      this.observer.observe(messageList, {
        childList: true,
        subtree: true,
        characterData: true
      })
    }
  }

  updateButtonVisibility() {
    const statusElements = document.querySelectorAll("[id^='msg-status-']")

    // Terminal states that mean streaming is complete
    const terminalStates = ['completed', 'cancelled', 'failed', 'truncated']

    const anyStreaming = Array.from(statusElements)
      .some(el => {
        const text = el.textContent.trim().toLowerCase()
        return text === "streaming" || text.includes("streaming")
      })

    const anyPending = Array.from(statusElements)
      .some(el => {
        const text = el.textContent.trim().toLowerCase()
        return text === "pending" || text.includes("pending")
      })

    console.log(`Streaming: ${anyStreaming}, Pending: ${anyPending}`)

    if (this.sendButton && this.cancelButton && this.backButton) {
      if (anyStreaming) {
        this.sendButton.classList.add("d-none")
        this.backButton.classList.add("d-none")
        this.cancelButton.classList.remove("d-none")
        this.disableChatTextarea()
        this.startStreamingTimeout()
      } else if (anyPending) {
        this.sendButton.classList.add("d-none")
        this.cancelButton.classList.add("d-none")
        this.backButton.classList.remove("d-none")
        this.disableChatTextarea()
        this.clearStreamingTimeout()
      } else {
        this.cancelButton.classList.add("d-none")
        this.backButton.classList.add("d-none")
        this.sendButton.classList.remove("d-none")
        this.enableChatTextarea()
        this.clearStreamingTimeout()
      }
    }
  }

  startStreamingTimeout() {
    // Only start timeout if not already running
    if (this.streamingTimeoutId) return

    this.streamingStartTime = Date.now()
    console.log("Starting streaming timeout watchdog")

    this.streamingTimeoutId = setTimeout(() => {
      console.warn("Streaming timeout reached - forcing UI recovery")
      this.forceRecovery()
    }, this.constructor.STREAMING_TIMEOUT)
  }

  clearStreamingTimeout() {
    if (this.streamingTimeoutId) {
      clearTimeout(this.streamingTimeoutId)
      this.streamingTimeoutId = null
      this.streamingStartTime = null
    }
  }

  forceRecovery() {
    console.warn("Force recovering from stuck streaming state")

    // Reset UI to send state
    if (this.sendButton && this.cancelButton && this.backButton) {
      this.cancelButton.classList.add("d-none")
      this.backButton.classList.add("d-none")
      this.sendButton.classList.remove("d-none")
      this.enableChatTextarea()
    }

    // Try to remove any lingering streaming cursors
    const cursors = document.querySelectorAll('.streaming-cursor')
    cursors.forEach(cursor => cursor.remove())

    this.clearStreamingTimeout()
  }

  disableChatTextarea() {
    if (this.chatTextarea) {
      this.chatTextarea.disabled = true
      this.chatTextarea.style.opacity = "0.6"
      this.chatTextarea.style.cursor = "not-allowed"
      console.log("Chat textarea disabled")
    }
  }

  enableChatTextarea() {
    if (this.chatTextarea) {
      this.chatTextarea.disabled = false
      this.chatTextarea.style.opacity = "1"
      this.chatTextarea.style.cursor = "text"
      console.log("Chat textarea enabled")
    }
  }

  refresh() {
    this.updateButtonVisibility()
  }
}